import React, { useEffect, useState } from "react";
import {
  Box,
  Grid,
  Paper,
  Typography,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Card,
  CardContent,
  styled,
  tableCellClasses,
  TablePagination,
} from "@mui/material";
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  LabelList,
  Legend,
} from "recharts";
import { axiosAPI } from "../../Components/AxiosComponents/Axios";
import SelectInput from "../../Components/InputComponents/SelectInput";

const StatCard = React.memo(({ title, value, Icon, color }) => (
  <Card
    sx={{
      display: "flex",
      flexDirection: "column",
      boxShadow: "none",
      // border: "2px solid #f0f0f0",
      borderRadius: 2,
      height: "100%",
      bgcolor: color || "#f0f0f0",
      boxShadow: "0 1px 4px rgba(0, 0, 0, 0.34)",
    }}
  >
    <CardContent
      sx={{
        flexGrow: 1,
        display: "flex",
        flexDirection: "column",
        gap: 1,
      }}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          justifyContent: "space-between",
        }}
      >
        <Typography
          sx={{
            fontFamily: "Geist, sans-serif",
            fontSize: "18px",
            color: "#1c1c1c",
            fontWeight: 600,
          }}
        >
          {title}
        </Typography>
        <Box
          sx={{
            background: "white",
            border: "2px solid lightgrey",
            borderRadius: "50%",
            p: 1,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          {Icon}
        </Box>
      </Box>

      <Typography
        sx={{
          fontFamily: "Geist, sans-serif",
          fontSize: "22px",
          color: "#212121",
          fontWeight: 600,
        }}
      >
        {value ?? "--"}
      </Typography>
    </CardContent>
  </Card>
));

const JobsPostedChart = ({ data, data2, yearOptions, year, setYear }) => (
  <Paper sx={{ boxShadow: "none" }}>
    <Grid container>
      <Grid item xs={12} sm={12} md={6} my={1}>
        <Box
          sx={{
            border: "2px solid lightgrey",
            borderRadius: 2,
            p: 2,
            my: 1,
            mx: { xs: 0, md: 1 },
            minHeight: { xs: "0px", md: "400px" },
          }}
        >
          <Grid container>
            <Grid item xs={12} sm={8} md={7} lg={8} sx={{ minHeight: "50px" }}>
              <Typography
                sx={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: "18px",
                  color: "#3e3e3e",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                New Clients
              </Typography>
            </Grid>
            <Grid item xs={12} sm={4} md={5} lg={4} mb={2}>
              <SelectInput
                label="Year"
                options={yearOptions}
                value={year}
                onChange={(e) => setYear(e.target.value)}
                fullWidth
              />
            </Grid>
            {data?.every((item) => item.TotalNewClients == 0) ? (
              <Grid
                item
                xs={12}
                mb={2}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "290px", // ensures vertical centering space
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{
                    fontSize: "40px",
                    background: "white",
                    p: 1,
                    borderRadius: 1,
                  }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                sm={12}
                sx={{ position: "relative", right: { xs: "0px", sm: "25px" } }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={data}
                    margin={{ top: 16, right: 16, bottom: 16, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="Quarter"
                      type="category"
                      tickFormatter={(v) => `Q${v}`}
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      type="number"
                      allowDecimals={false}
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip
                      formatter={(v) => [v, "New Clients"]}
                      labelFormatter={(l) => `Q${l}`}
                    />
                    <Legend />
                    <Bar
                      dataKey="TotalNewClients"
                      fill="#006CB8"
                      name="Total New Clients for Each Quarter"
                    >
                      <LabelList dataKey="TotalNewClients" position="top" />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>

      <Grid item xs={12} sm={12} md={6} my={1}>
        <Box
          sx={{
            border: "2px solid lightgrey",
            borderRadius: 2,
            p: 2,
            my: 1,
            mx: { xs: 0, md: 1 },
            minHeight: { xs: "0px", md: "400px" },
          }}
        >
          <Grid container>
            <Grid item xs={12} sm={8} md={7} lg={8} sx={{ minHeight: "50px" }}>
              <Typography
                sx={{
                  fontFamily: "Geist, sans-serif",
                  fontSize: "18px",
                  color: "#3e3e3e",
                  fontWeight: 500,
                  letterSpacing: "0.5px",
                }}
              >
                Top Clients
              </Typography>
            </Grid>

            {data2?.every((item) => item?.JobOpenings == 0) ? (
              <Grid
                item
                xs={12}
                mb={2}
                sx={{
                  display: "flex",
                  justifyContent: "center",
                  alignItems: "center",
                  minHeight: "290px", // ensures vertical centering space
                }}
              >
                <Typography
                  color="text.secondary"
                  sx={{ fontSize: "40px", background: "white" }}
                >
                  No data to show
                </Typography>
              </Grid>
            ) : (
              <Grid
                item
                xs={12}
                sm={12}
                sx={{ position: "relative", right: { xs: "0px", sm: "25px" } }}
              >
                <ResponsiveContainer width="100%" height={300}>
                  <BarChart
                    data={data2}
                    margin={{ top: 16, right: 16, bottom: 16, left: 8 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" />
                    <XAxis
                      dataKey="CD_Name"
                      type="category"
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <YAxis
                      type="number"
                      allowDecimals={false}
                      tick={{
                        fill: "#333",
                        fontSize: 14,
                        fontWeight: "400",
                         fontFamily: "Geist, sans-serif",
                      }}
                    />
                    <Tooltip />
                    <Legend />
                    <Bar
                      dataKey="JobOpenings"
                      fill="#006CB8"
                      name="Job Openings"
                    />
                  </BarChart>
                </ResponsiveContainer>
              </Grid>
            )}
          </Grid>
        </Box>
      </Grid>
    </Grid>
  </Paper>
);

const ClientReportView = ({
  clientStatData,
  mostCandidateDeployed,
  topClients,
  NewClientData,
  yearOptions,
  year,
  setYear,
}) => {
  const top4 = (mostCandidateDeployed ?? [])
    .slice()
    .sort((a, b) => (b?.DeployedCount ?? 0) - (a?.DeployedCount ?? 0))
    .slice(0, 4);

  return (
    <Box sx={{ p: 3 }}>
      <Typography
        variant="h5"
        sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
        mb={1}
        px={1}
      >
        Client Reports
      </Typography>

      <Grid container spacing={2} p={1}>
        {clientStatData?.map((item, idx) => (
          <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={idx}>
            <StatCard
              title={item.title}
              value={item.value}
              Icon={item?.Icon}
              color={item?.color}
            />
          </Grid>
        ))}
      </Grid>
      {top4?.length > 0 ? (
        <>
          <Grid container spacing={2} p={1}>
            <Grid item xs={12}>
              <Typography
                variant="h5"
                sx={{ fontWeight: 600, fontFamily: "Geist, sans-serif" }}
                my={1}
              >
                Most Candidate deployed Client
              </Typography>
            </Grid>
          </Grid>

          <Grid container spacing={5} p={1} justifyContent="flex-start" mb={3}>
            {top4.map((a, index) => {
              const labels = ["1st", "2nd", "3rd", "4th"];

              return (
                <Grid item xs={12} sm={6} md={4} lg={4} xl={3} key={index}>
                  <Box
                    sx={{
                      position: "relative",
                      boxShadow: 3,
                      borderRadius: 2,
                      p: 2,
                      textAlign: "center",
                      color: "white",
                      backgroundColor:
                        index == 0
                          ? `#367A91`
                          : index == 1
                          ? `#38446B`
                          : index == 2
                          ? `#14486C`
                          : index == 3
                          ? `#1B2E3C`
                          : "#1976d2",
                    }}
                  >
                    {/* Circle badge */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 10,
                        right: -20,
                        width: 45,
                        height: 45,
                        borderRadius: "50%",
                        backgroundColor: "white",
                        color:
                          index == 0
                            ? `#367A91`
                            : index == 1
                            ? `#38446B`
                            : index == 2
                            ? `#14486C`
                            : index == 3
                            ? `#1B2E3C`
                            : "#1976d2",
                        border: "4px solid",
                        borderColor:
                          index == 0
                            ? `#367A91`
                            : index == 1
                            ? `#38446B`
                            : index == 2
                            ? `#14486C`
                            : index == 3
                            ? `#1B2E3C`
                            : "#1976d2",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        fontWeight: "bold",
                        boxShadow: `none`,
                        fontSize: "0.9rem",
                      }}
                    >
                      {a?.DeployedCount}
                    </Box>

                    {/* Banner / Ribbon */}
                    <Box
                      sx={{
                        position: "absolute",
                        top: 0,
                        left: 0,
                        backgroundColor: "white",
                        color: "black",
                        px: 1.5,
                        py: 0.5,
                        fontSize: "0.8rem",
                        fontWeight: "bold",
                        borderRadius: "8px 0px 8px 0px",
                        boxShadow: 3,
                      }}
                    >
                      {labels[index]}
                    </Box>

                    {/* Title */}
                    <Typography
                      sx={{
                        fontFamily: "Geist, sans-serif",
                        letterSpacing: `0.5px`,
                        fontSize: "22px",
                        color: "white",
                        fontWeight: "600",
                        textTransform: "capitalize",
                      }}
                    >
                      {a?.CD_Name}
                    </Typography>
                  </Box>
                </Grid>
              );
            })}
          </Grid>
        </>
      ) : null}

      <JobsPostedChart
        data={NewClientData}
        data2={topClients}
        yearOptions={yearOptions}
        year={year}
        setYear={setYear}
      />
    </Box>
  );
};

export default ClientReportView;
